import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { VStack, HStack, StackItem } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { Button } from '@astryxdesign/core/Button';
import { TextInput } from '@astryxdesign/core/TextInput';
import { Card } from '@astryxdesign/core/Card';
import { Spinner } from '@astryxdesign/core/Spinner';
import { askAssistant, type AssistantAction, type ChatMessage } from '@/lib/assistant';

interface Turn extends ChatMessage {
  actions?: AssistantAction[];
}

/** Shared chat body for the client and owner assistants. */
export function AssistantChat({
  slug,
  scope,
  tenantId,
  suggestions,
  initialQuestion,
  onAction,
  intro,
}: {
  slug: string;
  scope: 'client' | 'owner';
  tenantId?: string;
  suggestions: string[];
  initialQuestion?: string;
  onAction?: (a: AssistantAction) => void;
  intro: string;
}) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [chips, setChips] = useState(suggestions);
  const endRef = useRef<HTMLLIElement>(null);
  const asked = useRef(false);

  const ask = useMutation({
    mutationFn: (history: Turn[]) => askAssistant(slug, scope, history.map(({ role, content }) => ({ role, content })), tenantId),
    onSuccess: (r) => {
      setTurns((t) => [...t, { role: 'assistant', content: r.reply, actions: r.actions }]);
      if (r.suggestions.length) setChips(r.suggestions);
    },
  });

  const send = (text: string) => {
    const q = text.trim();
    if (!q || ask.isPending) return;
    const next = [...turns, { role: 'user' as const, content: q.slice(0, 500) }];
    setTurns(next);
    setInput('');
    ask.mutate(next);
  };

  useEffect(() => {
    if (initialQuestion && !asked.current) {
      asked.current = true;
      send(initialQuestion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuestion]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [turns, ask.isPending]);

  return (
    <VStack gap={3}>
      <ul className="flex flex-col gap-3" aria-live="polite" aria-label="Диалог с помощником">
        <li>
          <Text color="secondary">{intro}</Text>
        </li>
        {turns.map((t, i) => (
          <li key={i} className={t.role === 'user' ? 'self-end max-w-[85%]' : 'self-start max-w-[92%]'}>
            <Card padding={3} variant={t.role === 'user' ? 'blue' : 'muted'}>
              <VStack gap={2}>
                <Text as="p" textWrap="pretty">
                  {t.content}
                </Text>
                {t.actions?.map((a, j) => (
                  <Button key={j} label={a.label} size="sm" variant="primary" onClick={() => onAction?.(a)} />
                ))}
              </VStack>
            </Card>
          </li>
        ))}
        {ask.isPending && (
          <li className="self-start">
            <Spinner size="sm" label="Помощник думает" />
          </li>
        )}
        {ask.isError && (
          <li>
            <Text color="secondary">{(ask.error as Error).message}</Text>
          </li>
        )}
        <li ref={endRef} aria-hidden />
      </ul>
      {chips.length > 0 && (
        <HStack gap={2} wrap="wrap">
          {chips.map((c) => (
            <Button key={c} label={c} size="sm" variant="secondary" isDisabled={ask.isPending} onClick={() => send(c)} />
          ))}
        </HStack>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
      >
        <HStack gap={2} vAlign="end">
          <StackItem size="fill">
            <TextInput label="Вопрос" isLabelHidden value={input} onChange={setInput} placeholder="Спросите что угодно о студии" />
          </StackItem>
          <Button type="submit" label="Спросить" variant="primary" isDisabled={!input.trim()} isLoading={ask.isPending} />
        </HStack>
      </form>
    </VStack>
  );
}
